import { Button, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, message } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { authSelector } from '../../redux/slices/authSlice'
import { apiAddCompanyZaloConfig, apiDeleteCompanyZaloConfig, apiGetListCompanies, apiListCompanyZaloConfig, apiUpdateCompanyZaloConfig } from '../../api'
import { ICompany } from '../../models/company.modal'
import defaultParams from '../../assets/constants/default_params'

type Config = {
  cpCode: string
  autoFriendInviteEnabled: boolean
  friendInviteMessage?: string
  telegramChatId?: string
  telegramBotToken?: string
  telegramMessageThreadId?: string
  rateLimitPerHour?: number
  rateLimitPerDay?: number
  cooldownSec?: number
  maxAttempts?: number
}

type FormValues = {
  cpCode: string
  autoFriendInviteEnabled: boolean
  friendInviteMessage?: string
  telegramChatId?: string
  telegramBotToken?: string
  telegramMessageThreadId?: string
  rateLimitPerHour?: number
  rateLimitPerDay?: number
  cooldownSec?: number
  maxAttempts?: number
}

export default function ZaloConfig() {
  const authState = useSelector(authSelector)
  // Only admin can manage configs
  const isAdmin: boolean = (authState?.account as any)?.isAdmin === true

  const [rows, setRows] = useState<Config[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editRow, setEditRow] = useState<Config | null>(null)
  const [companies, setCompanies] = useState<ICompany[]>([])
  const [form] = Form.useForm<FormValues>()

  // Load companies for the CP-code dropdown.
  useEffect(() => {
    apiGetListCompanies({ limit: defaultParams.MAX_LIMIT })
      .then((res: any) => setCompanies(res?.data?.companies ?? []))
      .catch(() => setCompanies([]))
  }, [])

  // In Add mode: exclude CPs that already have a config so we don't create duplicates.
  const cpOptions = useMemo(() => {
    const configured = new Set(rows.map(r => r.cpCode))
    return companies
      .filter(c => !!c.code)
      .filter(c => !!editRow || !configured.has(c.code as string))
      .map(c => ({
        value: c.code as string,
        label: `${c.name ?? ''} (${c.code})`,
      }))
  }, [companies, rows, editRow])

  const fetchConfigs = useCallback(async () => {
    setLoading(true)
    try {
      const res: any = await apiListCompanyZaloConfig({
        limit: pageSize,
        offset: (page - 1) * pageSize,
      })
      setRows(res?.data?.items ?? [])
      setTotal(res?.data?.total ?? 0)
    } catch (err: any) {
      message.error(err?.message || 'Tải danh sách cấu hình thất bại')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize])

  useEffect(() => {
    void fetchConfigs()
  }, [fetchConfigs])

  const openAdd = () => {
    setEditRow(null)
    form.resetFields()
    form.setFieldsValue({ autoFriendInviteEnabled: false })
    setModalOpen(true)
  }

  const openEdit = (row: Config) => {
    setEditRow(row)
    form.setFieldsValue({
      cpCode: row.cpCode,
      autoFriendInviteEnabled: row.autoFriendInviteEnabled,
      friendInviteMessage: row.friendInviteMessage,
      telegramChatId: row.telegramChatId,
      telegramBotToken: row.telegramBotToken,
      telegramMessageThreadId: row.telegramMessageThreadId,
      rateLimitPerHour: row.rateLimitPerHour,
      rateLimitPerDay: row.rateLimitPerDay,
      cooldownSec: row.cooldownSec,
      maxAttempts: row.maxAttempts,
    })
    setModalOpen(true)
  }

  const handleDelete = async (cpCode: string) => {
    try {
      await apiDeleteCompanyZaloConfig(cpCode)
      message.success(`Đã xóa cấu hình ${cpCode}`)
      void fetchConfigs()
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Xóa thất bại')
    }
  }

  const handleSave = async () => {
    let values: FormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    try {
      if (editRow) {
        await apiUpdateCompanyZaloConfig({ ...values, cpCode: editRow.cpCode })
        message.success('Cập nhật cấu hình thành công')
      } else {
        await apiAddCompanyZaloConfig(values)
        message.success('Thêm cấu hình thành công')
      }
      setModalOpen(false)
      void fetchConfigs()
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Lưu thất bại')
    }
  }

  const companyByCode = useMemo(() => {
    const map = new Map<string, ICompany>()
    companies.forEach(c => { if (c.code) map.set(c.code, c) })
    return map
  }, [companies])

  const columns = [
    {
      title: 'Đối tác',
      dataIndex: 'cpCode',
      key: 'cpCode',
      render: (cpCode: string) => {
        const c = companyByCode.get(cpCode)
        return c?.name ? `${c.name} (${cpCode})` : cpCode
      },
    },
    {
      title: 'Tự động mời kết bạn',
      dataIndex: 'autoFriendInviteEnabled',
      key: 'autoFriendInviteEnabled',
      render: (v: boolean) => <Switch checked={v} disabled />,
    },
    { title: 'Telegram Chat ID', dataIndex: 'telegramChatId', key: 'telegramChatId', render: (v: any) => v || '—' },
    { title: 'Giới hạn/giờ', dataIndex: 'rateLimitPerHour', key: 'rateLimitPerHour', render: (v: any) => v ?? '—' },
    { title: 'Giới hạn/ngày', dataIndex: 'rateLimitPerDay', key: 'rateLimitPerDay', render: (v: any) => v ?? '—' },
    { title: 'Cooldown (s)', dataIndex: 'cooldownSec', key: 'cooldownSec', render: (v: any) => v ?? '—' },
    { title: 'Số lần tối đa', dataIndex: 'maxAttempts', key: 'maxAttempts', render: (v: any) => v ?? '—' },
    ...(isAdmin
      ? [
          {
            title: 'Thao tác',
            key: 'actions',
            render: (_: any, row: Config) => (
              <Space>
                <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(row)}>
                  Sửa
                </Button>
                <Popconfirm
                  title={`Xóa cấu hình ${row.cpCode}?`}
                  description="Thao tác không thể hoàn tác. Xóa cả các bản ghi định dạng cũ (nếu có)."
                  okText="Xóa"
                  cancelText="Hủy"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => handleDelete(row.cpCode)}
                >
                  <Button icon={<DeleteOutlined />} size="small" danger>
                    Xóa
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ]

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }}>
        <h2 style={{ margin: 0 }}>Cấu hình Zalo theo đối tác</h2>
        {isAdmin && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            Thêm cấu hình
          </Button>
        )}
      </Space>

      <Table
        rowKey="cpCode"
        loading={loading}
        dataSource={rows}
        columns={columns as any}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, s) => {
            if (s !== pageSize) {
              setPage(1)
              setPageSize(s)
            } else {
              setPage(p)
            }
          },
          showTotal: (t) => `Tổng ${t} cấu hình`,
        }}
      />

      <Modal
        title={editRow ? `Sửa cấu hình — ${editRow.cpCode}` : 'Thêm cấu hình Zalo'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={editRow ? 'Cập nhật' : 'Thêm'}
        cancelText="Hủy"
        width={560}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="Đối tác (CP)"
            name="cpCode"
            rules={[{ required: true, message: 'Vui lòng chọn đối tác' }]}
            extra={editRow ? undefined : 'Chỉ hiển thị đối tác chưa có cấu hình Zalo'}
          >
            <Select
              disabled={!!editRow}
              showSearch
              placeholder="Chọn đối tác"
              options={cpOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              notFoundContent={editRow ? 'Không có đối tác' : 'Tất cả đối tác đã có cấu hình'}
            />
          </Form.Item>

          <Form.Item label="Tự động mời kết bạn" name="autoFriendInviteEnabled" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item
            label="Nội dung lời mời kết bạn"
            name="friendInviteMessage"
            extra="Để trống nếu không muốn gửi kèm lời chào"
          >
            <Input.TextArea rows={3} placeholder="Ví dụ: Xin chào, tôi là bên hỗ trợ, kết bạn để trao đổi nhé!" maxLength={200} showCount />
          </Form.Item>

          <Form.Item label="Telegram Chat ID" name="telegramChatId">
            <Input placeholder="-100xxxxxxxxx" />
          </Form.Item>

          <Form.Item
            label="Telegram Message Thread ID"
            name="telegramMessageThreadId"
            extra="Để trống nếu group Telegram không dùng topic"
          >
            <Input placeholder="12345" />
          </Form.Item>

          <Form.Item label="Telegram Bot Token" name="telegramBotToken">
            <Input.Password placeholder="Để trống nếu không đổi" />
          </Form.Item>

          <Form.Item label="Giới hạn lời mời / giờ" name="rateLimitPerHour">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Mặc định hệ thống" />
          </Form.Item>

          <Form.Item label="Giới hạn lời mời / ngày" name="rateLimitPerDay">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Mặc định hệ thống" />
          </Form.Item>

          <Form.Item label="Cooldown giữa 2 lần gửi (giây)" name="cooldownSec">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Mặc định hệ thống" />
          </Form.Item>

          <Form.Item label="Số lần thử tối đa" name="maxAttempts">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Mặc định hệ thống" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
